import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import type { Message } from '../types';
import { MessageContent } from '@/modules/chatbot/components/message-content';
import { AgentShadowMessage } from '@/modules/shadow/components/agent-shadow-message';
import { AttachmentChip } from './attachment-chip';

interface MessageBubbleProps {
  message: Message;
  className?: string;
}

const UserMessage = ({ message, className }: MessageBubbleProps) => {
  const timestamp = message.timestamp ? format(message.timestamp, 'MMM d, h:mm a') : null;
  return (
    <div className={cn('flex flex-col items-end justify-end mb-4 min-w-0 w-full', className)}>
      <div
        className='max-w-[65%] w-fit px-4 pt-4 pb-3 rounded-2xl border text-xs shadow-xs'
        style={{
          backgroundColor: 'var(--color-blue-50)',
          borderColor: 'var(--color-gray-300)',
          wordBreak: 'break-word',
          overflowWrap: 'anywhere'
        }}
      >
        <MessageContent
          content={message.content}
          contentAttributes={message.contentAttributes}
          additionalAttributes={message.additionalAttributes}
          contentType={message.contentType}
        />
        {message.attachments && message.attachments.length > 0 && (
          <div className='flex flex-wrap gap-1 mt-1.5'>
            {message.attachments.map((att, i) => (
              <AttachmentChip key={`${att.name}-${i}`} attachment={att} />
            ))}
          </div>
        )}
        {timestamp && <div className='place-self-end text-[0.625rem] text-gray-800'>{timestamp}</div>}
      </div>
      {/* Timestamp below user message */}
      {/* {timestamp && (
        <div className="text-xs text-muted-foreground mt-1 px-2">
          {timestamp} • Customer
        </div>
      )} */}
    </div>
  );
};

const UserMessageShadow = ({ message, className }: MessageBubbleProps) => {
  const timestamp = message.timestamp ? format(message.timestamp, 'MMM d, h:mm a') : null;
  return (
    <div className={cn('flex flex-col items-start justify-start mb-4 min-w-0 w-full', className)}>
      <div
        className='max-w-[65%] w-fit px-4 pt-4 pb-3 rounded-2xl border text-xs shadow-xs'
        style={{
          backgroundColor: 'var(--color-blue-50)',
          borderColor: 'var(--color-gray-300)',
          wordBreak: 'break-word',
          overflowWrap: 'anywhere'
        }}
      >
        <MessageContent
          content={message.content}
          contentAttributes={message.contentAttributes}
          additionalAttributes={message.additionalAttributes}
          contentType={message.contentType}
        />
        {timestamp && <div className='place-self-end text-[0.625rem] text-gray-800'>{timestamp}</div>}
      </div>
      {/* Timestamp below user message */}
      {/* {timestamp && (
        <div className="text-xs text-muted-foreground mt-1 px-2">
          {timestamp} • Customer
        </div>
      )} */}
    </div>
  );
};

const AIMessage = ({ message, className }: MessageBubbleProps) => {
  const { shadowEval, isError } = message;
  const timestamp = message.timestamp ? format(message.timestamp, 'MMM d, h:mm a') : null;

  // Use new AgentShadowMessage component if a shadow evaluation is present
  if (shadowEval) {
    return <AgentShadowMessage message={message} />;
  }

  // Fallback to old display for messages without shadow metadata
  return (
    <div className='flex flex-col items-start justify-end mb-4 min-w-0 w-full'>
      {/* Agent Message - Right Side */}
      <div className={cn('flex flex-col mb-2 min-w-0 w-full', className)}>
        <div
          className='max-w-[80%] px-4 pt-4 pb-3 rounded-2xl border text-sm'
          style={{
            backgroundColor: isError ? 'var(--color-red-50)' : 'var(--color-gray-100)',
            borderColor: isError ? 'var(--color-red-300)' : 'var(--color-gray-400)'
          }}
        >
          <MessageContent
            content={message.content}
            contentAttributes={message.contentAttributes}
            additionalAttributes={message.additionalAttributes}
            contentType={message.contentType}
          />
          {timestamp && <div className='place-self-end text-[0.625rem] text-gray-800'>{timestamp}</div>}
        </div>
      </div>
    </div>
  );
};

export const MessageBubble = ({ message, className }: MessageBubbleProps) => {
  if (message.role === 'user') {
    return <UserMessage message={message} className={className} />;
  }

  return <AIMessage message={message} className={className} />;
};

export const MessageBubbleShadow = ({ message, className }: MessageBubbleProps) => {
  if (message.role === 'user') {
    return <UserMessageShadow message={message} className={className} />;
  }

  return <AIMessage message={message} className={className} />;
};
